import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Compass, ArrowRight, UserPen } from 'lucide-react';
import type { CareerPathDiscovery } from './types';

interface CareerPathResultsProps {
  discovery: CareerPathDiscovery;
  onSelectPath: (title: string) => void;
  onSetManually?: () => void;
  onDismiss?: () => void;
}

export const CareerPathResults = ({
  discovery,
  onSelectPath,
  onSetManually,
  onDismiss,
}: CareerPathResultsProps) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Compass className="w-5 h-5 text-primary" />
        <h4 className="font-semibold">Career Path Suggestions</h4>
      </div>
      {onDismiss && (
        <Button size="sm" variant="ghost" onClick={onDismiss} className="text-muted-foreground">
          Dismiss
        </Button>
      )}
    </div>
    <p className="text-sm text-muted-foreground">{discovery.summary}</p>

    <div className="space-y-3">
      {discovery.paths.map((path, i) => (
        <div key={i} className="p-4 rounded-lg border bg-muted/30 hover:border-primary/50 transition-colors space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h5 className="font-semibold text-base">{path.title}</h5>
              <p className="text-sm text-muted-foreground mt-1">{path.rationale}</p>
            </div>
            <Button
              size="sm"
              onClick={() => onSelectPath(path.title)}
              className="gap-1 shrink-0"
            >
              Choose
              <ArrowRight className="w-3 h-3" />
            </Button>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {path.key_skills_to_develop.map((skill, j) => (
              <Badge key={j} variant="outline" className="bg-blue-500/5 text-blue-600 border-blue-500/30 text-xs">
                {skill}
              </Badge>
            ))}
            <Badge variant="outline" className="bg-muted text-muted-foreground text-xs">
              {path.timeline}
            </Badge>
          </div>

          <p className="text-xs text-muted-foreground italic">{path.alignment}</p>
        </div>
      ))}
    </div>

    {onSetManually && (
      <button
        onClick={onSetManually}
        className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
      >
        <UserPen className="w-3.5 h-3.5" />
        Or set your target role manually
      </button>
    )}
  </div>
);
